import Link from "next/link";
import type {
  CServicesContent,
  CServicesItem,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { cServicesTranslations } from "./CServices.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CServices.module.css";

/**
 * CServices - visitor view komponentu `c_dedicated_services`.
 *
 * Data source: `C_DEDICATED_SERVICES_CONTROLLER::getApiContent()` zwraca:
 *   { id, symbol, hero: {eyebrow, title, description}, categories: [{id, name, items[]}, ...] }
 *
 * Roznica od CCompanyDigitalization: karty POKATEGORYZOWANE - kazda kategoria (z m_dictionary
 * symbolu `c_services_category`) renderuje osobna sekcje z naglowkiem i wlasnym grid'em kart.
 * Kolejnosc kategorii dyktuje backend (m_dictionary_items.sort). Nieprzypisane childy trafiaja
 * do sekcji "Pozostale" (label z c_dedicated_services LANG).
 */
export function CServices({ content }: CServicesProps) {
  const t = useT(cServicesTranslations);

  if (!content || typeof content !== "object") {
    return <div className={styles.empty}>{t.emptyData}</div>;
  }

  const categories = Array.isArray(content.categories) ? content.categories : [];
  const hero = content.hero ?? { eyebrow: null, title: null, description: null };

  const hasHero =
    (hero.eyebrow && hero.eyebrow.trim() !== "") ||
    (hero.title && hero.title.trim() !== "") ||
    (hero.description && hero.description.trim() !== "");

  return (
    <div className={styles.root}>
      {hasHero ? (
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            {hero.eyebrow ? (
              <p className={styles.heroEyebrow}>{hero.eyebrow}</p>
            ) : null}
            {hero.title ? <h1 className={styles.heroTitle}>{hero.title}</h1> : null}
            {hero.description ? (
              <p className={styles.heroDescription}>{hero.description}</p>
            ) : null}
          </div>
        </section>
      ) : null}

      {categories.length === 0 ? (
        <div className={styles.empty}>{t.emptyList}</div>
      ) : (
        categories.map((cat) => (
          <section key={cat.id} className={styles.categorySection}>
            <h2 className={styles.categoryTitle}>{cat.name}</h2>
            <ul className={styles.cards}>
              {cat.items.map((item) => (
                <CatalogCard
                  key={item.id}
                  item={item}
                  readMoreLabel={t.readMore}
                  inPreparationLabel={t.inPreparationBadge}
                />
              ))}
            </ul>
          </section>
        ))
      )}

      {content.cta ? (
        <CtaBanner cta={content.cta} />
      ) : null}
    </div>
  );
}

function CtaBanner({ cta }: { cta: NonNullable<CServicesContent["cta"]> }) {
  const href = extractInternalHref(cta.button_url);
  return (
    <section className={styles.cta}>
      <div className={styles.ctaInner}>
        <h2 className={styles.ctaTitle}>{cta.title}</h2>
        {cta.description ? (
          <p className={styles.ctaDescription}>{cta.description}</p>
        ) : null}
        {href !== null ? (
          <Link href={href} className={styles.ctaButton}>
            {cta.button_label}
          </Link>
        ) : (
          <a href={cta.button_url} className={styles.ctaButton}>
            {cta.button_label}
          </a>
        )}
      </div>
    </section>
  );
}

export type CServicesProps = {
  content: CServicesContent;
};

function CatalogCard({
  item,
  readMoreLabel,
  inPreparationLabel,
}: {
  item: CServicesItem;
  readMoreLabel: string;
  inPreparationLabel: string;
}) {
  const href = extractInternalHref(item.href);

  const inner = (
    <>
      <span
        className={`${styles.cardDot} ${item.in_preparation ? styles.cardDotMuted : styles.cardDotActive}`}
        aria-hidden="true"
      />
      {item.in_preparation ? (
        <span className={styles.cardBadge}>{inPreparationLabel}</span>
      ) : null}
      {item.icon ? (
        <span className={styles.cardIconWrap} aria-hidden="true">
          <i className={`nc-icon-outline ${item.icon}`} />
        </span>
      ) : null}
      <div className={styles.cardBody}>
        <h3 className={styles.cardTitle}>{item.name}</h3>
        {item.description ? (
          <p className={styles.cardDesc}>{item.description}</p>
        ) : null}
        <span className={styles.cardCta}>
          {readMoreLabel} <span aria-hidden="true">→</span>
        </span>
      </div>
    </>
  );

  return (
    <li className={styles.card}>
      {href !== null ? (
        <Link href={href} className={styles.cardLink}>
          {inner}
        </Link>
      ) : (
        <a href={item.href} className={styles.cardLink}>
          {inner}
        </a>
      )}
    </li>
  );
}
